// Route constants
export const ROUTE_PATHS = {
  HOME: "/",
  PORTFOLIO: "/portfolio",
  ABOUT: "/about",
  SERVICES: "/services",
  CONTACT: "/contact",
} as const;

// Portfolio Projects data
export interface Project {
  id: string;
  title: string;
  titleAr: string;
  client: string;
  category: "brand" | "corporate" | "social";
  year: string;
  description: string;
  descriptionAr: string;
  colors: string[];
  tags: string[];
  tagsAr: string[];
  featured: boolean;
  gradient: string;
  icon: string;
}

export const PROJECTS: Project[] = [
  {
    id: "2",
    title: "Jimmy Eddition",
    titleAr: "الشب جيمي",
    client: "الشب جيمي",
    category: "brand",
    year: "2026",
    description: "Logo for a content creator and production manager at Al-Shayeb Company.",
    descriptionAr: "لوجو كونتنت كريتور ومدير انتاج في شركة الشايب",
    colors: ["#00807A", "#1e1b4b", "#000000"],
    tags: ["Creative", "Artistic", "Modern"],
    tagsAr: ["إبداعي", "فني", "حديث"],
    featured: true,
    gradient: "from-indigo-800 to-violet-950",
    icon: "/images/work4.jpg",
  },
  {
    id: "1",
    title: "EAM Educational platform",
    titleAr: "منصة تعليمية جامعية",
    client: "AI Agamy Material",
    category: "corporate",
    year: "2026",
    description: "Visual identity for a university educational platform.",
    descriptionAr: "منصة تعليمية جامعية",
    colors: ["#00807A", "#0f172a", "#000000"],
    tags: ["Systems", "Corporate", "Identity"],
    tagsAr: ["أنظمة", "شركات", "هوية"],
    featured: true,
    gradient: "from-violet-600 to-fuchsia-900",
    icon: "/images/work1.jpg",
  },
  {
    id: "3",
    title: "Bahaa Gamal Posters",
    titleAr: "أ/ بهاء جمال - بوستر تعليمي",
    client: "Bahaa Gamal",
    category: "social",
    year: "2026",
    description: "Professional social media poster design for educational content.",
    descriptionAr: "تصميم بوستر سوشيال ميديا احترافي للمحتوى التعليمي.",
    colors: ["#00807A", "#1e293b", "#000000"],
    tags: ["Educational", "Social Media", "Poster"],
    tagsAr: ["تعليمي", "سوشيال ميديا", "بوستر"],
    featured: true,
    gradient: "from-emerald-600 to-teal-900",
    icon: "/images/work5.jpg",
  },
];

// Testimonials
export interface Testimonial {
  id: string;
  name: string;
  nameAr: string;
  role: string;
  roleAr: string;
  company: string;
  text: string;
  textAr: string;
  rating: number;
  avatar: string;
}

export const TESTIMONIALS: Testimonial[] = [
  {
    id: "1",
    name: "Jimmy",
    nameAr: "جيمي",
    role: "Creative Director",
    roleAr: "المدير الإبداعي",
    company: "Jimmy Edition",
    text: "The logo perfectly captured our brand's vision. The process was smooth, collaborative, and the result exceeded all expectations.",
    textAr: "أحاط الشعار برؤية علامتنا التجارية بشكل مثالي. كانت العملية سلسة وتعاونية والنتيجة فاقت جميع التوقعات.",
    rating: 5,
    avatar: "JE",
  },
  {
    id: "2",
    name: "Hany",
    nameAr: "هاني",
    role: "Founder",
    roleAr: "المؤسس",
    company: "Hany Identity",
    text: "Exceptional attention to detail and a deep understanding of what our brand stands for. Highly recommended!",
    textAr: "اهتمام استثنائي بالتفاصيل وفهم عميق لما تمثله علامتنا. أنصح به بشدة!",
    rating: 5,
    avatar: "HY",
  },
];

// Services data
export interface ServiceTier {
  id: string;
  titleKey: string;
  descKey: string;
  price: string;
  features: { en: string[]; ar: string[] };
  popular: boolean;
  color: string;
}

export const SERVICE_TIERS: ServiceTier[] = [
  {
    id: "basic",
    titleKey: "basicTitle",
    descKey: "basicDesc",
    price: "300 - 500 EGP",
    features: {
      en: [
        "Logo concepts",
        "2 revision rounds",
        "PNG & JPG delivery",
        "3-5 days delivery",
      ],
      ar: [
        "أفكار لوجو",
        "جولتان من المراجعة و التعديل",
        "تسليم الملف بصيغ PNG + JPG",
        "تسليم 3–5 أيام",
      ],
    },
    popular: false,
    color: "border-border",
  },
  {
    id: "pro",
    titleKey: "proTitle",
    descKey: "proDesc",
    price: "500 - 700 EGP",
    features: {
      en: [
        "Revisions until satisfied",
        "Full files (PNG + JPG + SVG)",
        "Mockup included",
        "4-7 days delivery",
      ],
      ar: [
        "تعديلات لحد ما نوصل لنتيجة مرضية",
        "ملفات كاملة (PNG + JPG + SVG)",
        "Mockup",
        "تسليم 4–7 أيام",
      ],
    },
    popular: true,
    color: "border-primary",
  },
  {
    id: "premium",
    titleKey: "premiumTitle",
    descKey: "premiumDesc",
    price: "800 - 1200 EGP",
    features: {
      en: [
        "Professional logo design",
        "Colors & visual identity",
        "Social Media Kit",
        "Business Card",
        "Professional Logo animation",
        "5-14 days delivery",
      ],
      ar: [
        "تصميم لوجو احترافي",
        "تحديد ألوان وهوية بصرية",
        "Social Media Kit",
        "Business Card",
        "Logo animation احترافي",
        "تسليم خلال 5–14 أيام",
      ],
    },
    popular: false,
    color: "border-accent",
  },
];

// FAQ data
export interface FAQ {
  questionEn: string;
  questionAr: string;
  answerEn: string;
  answerAr: string;
}

export const FAQS: FAQ[] = [
  {
    questionEn: "How long does a logo project take?",
    questionAr: "كم يستغرق مشروع الشعار؟",
    answerEn:
      "Timelines vary by package. Essentials take 7 days, Professional 14 days, and Premium 21 days from the project kickoff call.",
    answerAr:
      "تختلف الجداول الزمنية حسب الحزمة. تستغرق الأساسيات 7 أيام، والاحترافية 14 يوماً، والمتميزة 21 يوماً من مكالمة انطلاق المشروع.",
  },
  {
    questionEn: "What file formats will I receive?",
    questionAr: "ما صيغ الملفات التي سأحصل عليها؟",
    answerEn: "All packages include PNG, SVG, PDF, and EPS files. Premium packages also include editable AI source files.",
    answerAr: "تشمل جميع الحزم ملفات PNG و SVG و PDF و EPS. وتشمل الحزم المتميزة أيضاً ملفات AI قابلة للتحرير.",
  },
  {
    questionEn: "Do you work with international clients?",
    questionAr: "هل تعمل مع عملاء دوليين؟",
    answerEn: "Absolutely! I work with clients worldwide and have experience designing for diverse markets including the Middle East and Arabic-speaking regions.",
    answerAr: "بالطبع! أعمل مع عملاء من جميع أنحاء العالم ولديّ خبرة في التصميم للأسواق المتنوعة بما في ذلك الشرق الأوسط والمناطق الناطقة بالعربية.",
  },
  {
    questionEn: "What if I'm not happy with the designs?",
    questionAr: "ماذا لو لم أكن راضياً عن التصاميم؟",
    answerEn: "Your satisfaction is guaranteed. Professional and Premium packages include unlimited revisions until you're completely happy with the result.",
    answerAr: "رضاك مضمون. تشمل الحزم الاحترافية والمتميزة مراجعات غير محدودة حتى تكون راضياً تماماً عن النتيجة.",
  },
];
